import { Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { BookingParameters, codetype } from '../entities/bookingParameters';

/**
 * Used to map query parameters to a usable bookingParameters object.
 * Known and used params are startDate, endDate and ages[] and promotionCode
 */
@Injectable({
  providedIn: 'root'
})
export class ParamsMapperService {
  /**
   * tries to map query parameters onto a given template and return the result.
   * If params could not be parsed, they will be omitted.
   *
   * @param params the query parameter ParamMap from a router
   * @param bookingParameters the 'vanilla' bookingParameters to use as a template
   * @return bookingParameters with updated params as parsed from the query params
   * @throws {DateUnparsableException | DateRangeImpossibleException} if params are malformed and can not be recovered from
   */
  parseParams(params: ParamMap, bookingParameters: BookingParameters): BookingParameters {
    // copy params to not push unexpected changes to the adress pointer
    const localParams = bookingParameters.copy();
    let changedParams = false;
    if (params.has('startDate')) {
      changedParams = true;
      localParams.start = this.getDateFromString(params.get('startDate'));
      if (!params.has('endDate')) {
        localParams.end = new Date(localParams.start.getTime());
        localParams.end.setHours(23, 59, 59, 999);
      }
    }

    if (params.has('endDate')) {
      changedParams = true;
      localParams.end = this.getDateFromString(params.get('endDate'));
      if (!params.has('startDate')) {
        localParams.start = new Date(localParams.end.getTime());
      }
      localParams.end.setHours(23, 59, 59, 999);
    }

    if (localParams.start?.getTime() > localParams.end?.getTime()) {
      throw new DateRangeImpossibleException(
        'The start and end date combination can not be used as a daterange'
      );
    }

    if (params.getAll('personAges[]').length) {
      changedParams = true;
      localParams.ages = this.getPersonAgesFromParams(params);
    }
    if (localParams.ages?.length === 0) {
      localParams.ages.push(36);
    }

    if (params.has('promotionCode')) {
      changedParams = true;
      localParams.codes = [{ type: codetype.promo, text: params.get('promotionCode').trim() }];
    }

    if (changedParams) {
      localParams.userDefinedSearch = true;
    }

    return localParams;
  }

  /**
   * Returns an array of number-values
   * @param params
   */
  private getPersonAgesFromParams(params: ParamMap): Array<number> {
    const personAges = [];

    const personAgesParams = params.getAll('personAges[]');
    personAgesParams.forEach(age => {
      if (Number.isInteger(parseInt(age))) {
        if (+age < 1) {
          personAges.push(1);
        } else if (+age > 99) {
          personAges.push(36);
        } else {
          personAges.push(+age);
        }
      }
    });
    return personAges;
  }

  /**
   * parses a given datestring and returns a Date Object.
   * Will only parse dates formatted like 2021-12-20 or 2456-01-31
   * improvement: will ignore 2022-02-29, 30, 31. Additional logic can be applied after the regex
   * @param datestring the queryParam string to parse
   */
  private getDateFromString(datestring: string): Date {
    // this regex checks if:
    // (^) must match starting on the first character
    // (\d{4}) followed by 4 digits
    // (-) a dash
    // (0[1-9]|1[012]) 2 digits ranging form 01 until 12,
    // (-) a dash
    // ([0-2][0-9]|3[0-1]) 2 digits ranging from 01 until 31,
    // ($) string MUST end here, no addidional chars after here are allowed
    // "2021-01-23" its a match!
    // "asdf-12-12" nope, "2021-01-23T00:00:00" nope
    if (datestring.trim().match(/^\d{4}-(0[1-9]|1[012])-([0-2][0-9]|3[0-1])$/)) {
      const date = new Date(datestring.trim());
      date.setHours(0, 0, 0, 0);
      return date;
    }
    throw new DateUnparsableException(
      `Could not parse ${datestring} as it does not match RegEx expectations`
    );
  }
}

export class DateUnparsableException extends Error {
  constructor(msg: string) {
    super(msg);
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, DateUnparsableException.prototype);
  }
}

export class DateRangeImpossibleException extends Error {
  constructor(msg: string) {
    super(msg);
    // Set the prototype explicitly.
    Object.setPrototypeOf(this, DateRangeImpossibleException.prototype);
  }
}
